import {
    createTimeout,
    EventListener,
    executeOnce,
    ExtendedMap,
    isArrayNotEmpty,
    ManagedPromise,
    objectEntries,
    Queue,
    type ReadonlyNonEmptyArray,
    type ReadonlyRecord,
} from '../../utils'

import { Capability, ConnectwareError, type CybusService, type CybusServiceDeviation, type StatusType } from '../../domain'

import {
    type AuthenticatedResource,
    type BackendEventStreamResponseContent,
    type BackendPath,
    type BackendQueryParameters,
    createNonCountMapper,
    type DeviationsSubscription,
    mapRSTResourceStatusViewCurrentStatusType,
    mapRSTResourceStatusViewId,
    type ServiceDeviationsSupportedType,
    type ServicesDeviationsSupportedType,
    type ServicesResolvedDeviationsSupportedType,
    type StatusSupportedType,
    type StatusTypeSubscription,
} from '../Connectware'
import { RSTWebSocketWrapper, type RSTWebSocketWrapperOptions, type RSTWrappedWebSocket } from './Wrapper'

type RSTPaths = BackendPath<'resource-status-tracking'>

export type RSTBaseSubscriptionOptions =
    & Pick<RSTWebSocketWrapperOptions<RSTPaths>, 'origin' | 'reconnectTimeout'>
    & ReadonlyRecord<'cacheLifespan' | 'pageSize', number>

type MinimalArgs = [string, ...unknown[]]
type ArgParts<A extends MinimalArgs> = A extends [infer Type, ...infer Extra] ? Readonly<{ type: Type, extra: Extra }> : never
const extractArgsParts = <A extends MinimalArgs>([type, ...extra]: A): ArgParts<A> => ({ type, extra } as ArgParts<A>)

type SubscriptionRequest<A extends MinimalArgs> = Readonly<{ args: A, listener: EventListener<void>, promise: ManagedPromise<VoidFunction, ConnectwareError> }>

class RequestsManager<A extends MinimalArgs> {
    /**
     * The pending requests that need to be fetched
     */
    private readonly requests: SubscriptionRequest<A>[] = []

    constructor (private readonly pageSize: number) {}

    add (...args: A): [eventsListener: Pick<EventListener<void>, 'on'>, notifyDisuse: Promise<VoidFunction>] {
        const request: SubscriptionRequest<A> = { args, listener: new EventListener(), promise: new ManagedPromise() }

        this.requests.push(request)

        return [request.listener, request.promise.promise]
    }

    extractNextBatch (): SubscriptionRequest<A>[] {
        const [first, ...next] = this.requests.splice(0, this.requests.length)

        if (!first) {
            /** There is nothing here, just give up */
            return []
        }

        const { type } = extractArgsParts(first.args)
        const nextBatch: SubscriptionRequest<A>[] = [first]

        next.forEach((request) => {
            /**
             * Sockets with multiple types may one day have different paths again
             * So all the requests need to be of the same type in order to ensure internal consistency when creating sockets
             * This could be improved upon, but it is such a marginal case that improving may not be worth-while
             */
            if (nextBatch.length < this.pageSize && extractArgsParts(request.args).type === type) {
                nextBatch.push(request)
            } else {
                this.requests.push(request)
            }
        })

        return nextBatch
    }
}

class SocketsManager<A extends MinimalArgs, P extends RSTPaths> {
    /**
     * Sockets can be disconnected and reconnected as they are in use or disuse
     * This objects holds all of them for re-use
     */
    private readonly sockets = new Map<string, RSTWrappedWebSocket<P>>()

    constructor (
        private readonly mapId: (...args: ArgParts<A>['extra']) => string,
        private readonly mapQueryParameters: (ids: readonly string[]) => BackendQueryParameters<P>,
        private readonly sockerWrapperOptions: Pick<RSTBaseSubscriptionOptions, 'origin' | 'reconnectTimeout'>,
        private readonly SocketWrapper: new<Path extends RSTPaths>(options: RSTWebSocketWrapperOptions<Path>) => RSTWrappedWebSocket<Path>,
        private readonly pathsConfiguration: ReadonlyRecord<ArgParts<A>['type'], AuthenticatedResource<P>>
    ) {}

    protected createSocket (type: ArgParts<A>['type'], params: BackendQueryParameters<P>): RSTWrappedWebSocket<P> {
        const { origin, reconnectTimeout } = this.sockerWrapperOptions
        const { path } = this.pathsConfiguration[type]

        return new this.SocketWrapper({ origin, path, params, reconnectTimeout })
    }

    get (args: A): RSTWrappedWebSocket<P> | null {
        return this.sockets.get(this.mapId(...extractArgsParts(args).extra)) ?? null
    }

    create (requests: ReadonlyNonEmptyArray<A>): RSTWrappedWebSocket<P> {
        const ids = Array.from(requests.reduce((s, r) => s.add(this.mapId(...extractArgsParts(r).extra)), new Set<string>()))

        const { type } = extractArgsParts(requests[0])
        const socket = this.createSocket(type, this.mapQueryParameters(ids))

        for (const id of ids) {
            this.sockets.set(id, socket)
        }

        return socket
    }
}

class Cache<A extends MinimalArgs, P extends RSTPaths, V> {
    /**
     * The results that are cached for up to the lifespan
     * In case they are needed to be retrieved
     * @see {cacheLifespan}
     *
     * Why is this cache wrapped in a another object?
     * So the cache clearing comparisons does **not**
     * clear previous value by mistake if its a primitive value
     */
    private readonly results = new Map<`${ArgParts<A>['type']}_${string}`, ReadonlyRecord<'value', V | ConnectwareError>>()

    constructor (
        private readonly cacheLifespan: number,
        private readonly extractIdFromArgs: (...args: ArgParts<A>['extra']) => string,
        private readonly extractIdFromData: (data: BackendEventStreamResponseContent<P>) => string,
        private readonly mapCacheResult: (data: BackendEventStreamResponseContent<P>) => V
    ) {}

    set (type: ArgParts<A>['type'], args: ArgParts<A>['extra'], data: BackendEventStreamResponseContent<P> | ConnectwareError): void {
        const key = `${type}_${ConnectwareError.is(data) ? this.extractIdFromArgs(...args) : this.extractIdFromData(data)}` as const

        /**
         * Cache value is wrapped so the timeout validation only drops it by reference
         */
        const cache = { value: ConnectwareError.is(data) ? data : this.mapCacheResult(data) }

        this.results.set(key, cache)

        createTimeout(() => {
            if (this.results.get(key) === cache) {
                this.results.delete(key)
            }
        }, this.cacheLifespan)
    }

    get (type: ArgParts<A>['type'], ...args: ArgParts<A>['extra']): V | ConnectwareError | null {
        return this.results.get(`${type}_${this.extractIdFromArgs(...args)}`)?.value ?? null
    }
}

type SubscriptionStrategy<A extends MinimalArgs, P extends RSTPaths, V> = Readonly<{
    requests: RequestsManager<A>
    sockets: SocketsManager<A, P>
    isEventRelevant: (event: BackendEventStreamResponseContent<P>, ...args: ArgParts<A>['extra']) => boolean
    cache: Cache<A, P, V>
}>

abstract class RSTBaseSubscription {
    private readonly queue = new Queue()

    protected readonly SocketWrapper: new<Path extends RSTPaths>(options: RSTWebSocketWrapperOptions<Path>) => RSTWrappedWebSocket<Path> = RSTWebSocketWrapper

    constructor (protected readonly options: RSTBaseSubscriptionOptions) {}

    /**
     * This function creates a subscription
     * Calls the passed handler as messages arrive
     * And sets cache accordingly as needed
     */
    protected subscribe<A extends MinimalArgs, P extends RSTPaths, V = never> ({
        args,
        strategy: { requests, sockets, cache, isEventRelevant },
        handler,
    }: Readonly<{ args: A, strategy: SubscriptionStrategy<A, P, V>, handler: VoidFunction }>): Promise<VoidFunction> {
        const [listener, socketConnectionPromise] = requests.add(...args)

        const stopListening = listener.on(handler)

        /**
         * Use the queue to bunch up requests whenever possible
         */
        void this.queue.push(async () => {
            const batch = requests.extractNextBatch()

            const requestsGroupedBySockets = new ExtendedMap<RSTWrappedWebSocket<P>, SubscriptionRequest<A>[]>()
            const socketless: Readonly<{ requests: SubscriptionRequest<A>[], args: A[] }> = { requests: [], args: [] }

            /**
             * Go over each request
             * If there is no socket associated with each request, then ensure there will be one
             * But try to re-use previous sockets (that may still be active thus not needing reconnection)
             */
            for (const request of batch) {
                const socket = sockets.get(request.args)

                if (socket) {
                    requestsGroupedBySockets.set(socket, [...requestsGroupedBySockets.getDefault(socket, []), request])
                } else {
                    socketless.requests.push(request)
                    socketless.args.push(request.args)
                }
            }

            if (isArrayNotEmpty(socketless.args)) {
                /**
                 * Create a single socket for all missing elements
                 */
                requestsGroupedBySockets.set(sockets.create(socketless.args), socketless.requests)
            }

            /**
             * Process each group of requests associated with a WebSocket
             * And resolve each request by spinning up the socket
             */
            for (const [socket, socketRequests] of requestsGroupedBySockets) {
                for (const request of socketRequests) {
                    request.promise.resolve(
                        socket.on((event) => {
                            const { type, extra } = extractArgsParts(request.args)

                            cache.set(type, extra, event)

                            if (ConnectwareError.is(event) || isEventRelevant(event, ...extra)) {
                                /**
                                 * If its an error, just send it to everyone possibly with-in the given socket
                                 * If its an event, then check if that event is relevant to the given handler
                                 *
                                 * If either are true, then trigger the listener
                                 */
                                request.listener.trigger()
                            }
                        })
                    )
                }
            }
        })

        return socketConnectionPromise.then((notifySocketDisuse) => {
            return () => {
                stopListening()
                notifySocketDisuse()
            }
        })
    }
}

type WithHandler<A extends unknown[], HandlerArgs extends unknown[] = never[]> = [...args: A, handler: (...args: HandlerArgs) => void]

type StatusTypeArgs = [type: StatusSupportedType, id: string]
export class RSTStatusTypeSubscription extends RSTBaseSubscription implements StatusTypeSubscription {
    /**
     * The executeOnce is here to delay the intake of baseclass properties
     * This allows tests to inject their own values
     */
    private readonly createStatusTypeStrategy = executeOnce(
        (): SubscriptionStrategy<StatusTypeArgs, '/api/v2/resources/states', StatusType> => ({
            requests: new RequestsManager(this.options.pageSize),
            sockets: new SocketsManager(
                (id) => id,
                (resourceIds) => ({ resourceIds }),
                this.options,
                this.SocketWrapper,
                {
                    connection: { path: '/api/v2/resources/states', capability: Capability.CONNECTION_READ },
                    connections: { path: '/api/v2/resources/states', capability: Capability.CONNECTIONS_READ },
                    endpoint: { path: '/api/v2/resources/states', capability: Capability.ENDPOINT_READ },
                    endpoints: { path: '/api/v2/resources/states', capability: Capability.ENDPOINTS_READ },
                    mapping: { path: '/api/v2/resources/states', capability: Capability.MAPPING_READ },
                    mappings: { path: '/api/v2/resources/states', capability: Capability.MAPPINGS_READ },
                    nodes: { path: '/api/v2/resources/states', capability: Capability.NODES_READ },
                    server: { path: '/api/v2/resources/states', capability: Capability.SERVER_READ },
                    servers: { path: '/api/v2/resources/states', capability: Capability.SERVERS_READ },
                }
            ),
            cache: new Cache(
                this.options.cacheLifespan,
                (id) => id,
                (data) => mapRSTResourceStatusViewId(data.resource),
                (data) => mapRSTResourceStatusViewCurrentStatusType(data.state)
            ),
            isEventRelevant: (content, id) => content.resource.id === id,
        })
    )

    subscribeToStatusType (...[type, id, handler]: WithHandler<StatusTypeArgs>): Promise<VoidFunction> {
        return this.subscribe({ args: [type, id], strategy: this.createStatusTypeStrategy(), handler })
    }

    useCachedStatusType (...args: StatusTypeArgs): StatusType | ConnectwareError | null {
        return this.createStatusTypeStrategy().cache.get(...args)
    }
}

type ServiceDeviationsCountArgs = [type: ServiceDeviationsSupportedType, serviceId: CybusService['id']]
type ServicesDeviationsCountArgs = [type: ServicesDeviationsSupportedType]
type ResolvedDeviationsArgs = [type: ServicesResolvedDeviationsSupportedType]

export class RSTDeviationsSubscription extends RSTBaseSubscription implements DeviationsSubscription {
    private static readonly mapServiceId = createNonCountMapper<BackendEventStreamResponseContent<'/api/v2/resources/states/deviations/count'>, string>(
        ({ serviceId }) => serviceId
    )

    /**
     * The executeOnce is here to delay the intake of baseclass properties
     * This allows tests to inject their own values
     */
    private readonly createServiceDeviationStrategy = executeOnce(
        (): SubscriptionStrategy<ServiceDeviationsCountArgs, '/api/v2/resources/states/deviations/count', number> => ({
            requests: new RequestsManager(this.options.pageSize),
            sockets: new SocketsManager(
                (id) => id,
                (ids) => ({ ids }),
                this.options,
                this.SocketWrapper,
                {
                    connections: { path: '/api/v2/resources/states/deviations/count', capability: Capability.CONNECTIONS_READ },
                    endpoints: { path: '/api/v2/resources/states/deviations/count', capability: Capability.ENDPOINTS_READ },
                    mappings: { path: '/api/v2/resources/states/deviations/count', capability: Capability.MAPPINGS_READ },
                    nodes: { path: '/api/v2/resources/states/deviations/count', capability: Capability.NODES_READ },
                    resourceDeviation: { path: '/api/v2/resources/states/deviations/count', capability: Capability.DEVIATIONS_READ },
                    servers: { path: '/api/v2/resources/states/deviations/count', capability: Capability.SERVERS_READ },
                    service: { path: '/api/v2/resources/states/deviations/count', capability: Capability.SERVICE_READ },
                    services: { path: '/api/v2/resources/states/deviations/count', capability: Capability.SERVICES_READ },
                }
            ),
            cache: new Cache(
                this.options.cacheLifespan,
                (id) => id,
                RSTDeviationsSubscription.mapServiceId,
                createNonCountMapper(({ count }) => count)
            ),
            isEventRelevant: (content, id) => RSTDeviationsSubscription.mapServiceId(content) === id,
        })
    )

    private readonly getServicesDeviationsWrapper = this.createSocketWrapperGetter({
        connections: { path: '/api/v2/resources/states/deviations/count', capability: Capability.CONNECTIONS_READ },
        deviatedServices: { path: '/api/v2/resources/states/deviations/count', capability: Capability.DEVIATIONS_READ },
        endpoints: { path: '/api/v2/resources/states/deviations/count', capability: Capability.ENDPOINTS_READ },
        mappings: { path: '/api/v2/resources/states/deviations/count', capability: Capability.MAPPINGS_READ },
        nodes: { path: '/api/v2/resources/states/deviations/count', capability: Capability.NODES_READ },
        resourceDeviation: { path: '/api/v2/resources/states/deviations/count', capability: Capability.DEVIATIONS_READ },
        servers: { path: '/api/v2/resources/states/deviations/count', capability: Capability.SERVERS_READ },
        serviceDeviations: { path: '/api/v2/resources/states/deviations/count', capability: Capability.DEVIATIONS_READ },
        services: { path: '/api/v2/resources/states/deviations/count', capability: Capability.SERVICES_READ },
    })

    private readonly getDeviationsResolutionWrapper = this.createSocketWrapperGetter({
        resolvedServiceDeviations: { path: '/api/v2/resources/states/deviations/services', capability: Capability.DEVIATIONS_READ },
    })

    private createSocketWrapperGetter<Type extends string, Path extends BackendPath> (
        paths: ReadonlyRecord<Type, AuthenticatedResource<Path>>
    ): (type: Type) => RSTWrappedWebSocket<Path> {
        const wrapperCreators = objectEntries<ReadonlyRecord<Type, AuthenticatedResource<Path>>>(paths).reduce(
            (r, [type, configuration]) => ({
                ...r,
                /**
                 * The executeOnce is here to only create the wrappers when they are first setup
                 */
                [type]: executeOnce(() => {
                    const { origin, reconnectTimeout } = this.options
                    return new this.SocketWrapper({ origin, path: configuration.path, params: {}, reconnectTimeout })
                }),
            }),
            {} as ReadonlyRecord<Type, () => RSTWrappedWebSocket<Path>>
        )

        return (type) => wrapperCreators[type]()
    }

    useCachedServiceDeviationsCount (...args: ServiceDeviationsCountArgs): number | ConnectwareError | null {
        return this.createServiceDeviationStrategy().cache.get(...args)
    }

    subscribeToServiceDeviationsCount (...[type, serviceId, handler]: WithHandler<ServiceDeviationsCountArgs>): Promise<VoidFunction> {
        return this.subscribe({ args: [type, serviceId], strategy: this.createServiceDeviationStrategy(), handler })
    }

    subscribeToServicesDeviationsCount (...[type, handler]: WithHandler<ServicesDeviationsCountArgs>): Promise<VoidFunction> {
        return this.getServicesDeviationsWrapper(type).on(() => handler())
    }

    subscribeToResolvedDeviations (
        ...[type, handler]: WithHandler<ResolvedDeviationsArgs, [event: CybusServiceDeviation | ConnectwareError]>
    ): Promise<VoidFunction> {
        return this.getDeviationsResolutionWrapper(type).on((event) => {
            if (ConnectwareError.is(event)) {
                /** Error happened, so just pass it over */
                handler(event)
            } else if (event.service?.deviated === false) {
                /** Deviated service was resolved  */
                handler({ id: event.service.serviceId })
            }
        })
    }
}
